/**
 * Kom i gang (#294): GET /onboarding, the steps from the company's saved facts, and the caller's own
 * pause of the guide. The hub, the guide beside a page and Kontroll's waiting state read this one
 * answer. A pause changes no step (ONB-03).
 */
import { useAuth } from '@/auth/store';
import { type RuleProfile, useTenant } from '@/shell/tenant';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, get, put } from './client';

export type Phase = 'opprett' | 'kontroller' | 'rapporter';
export type StepKey =
  | 'arbeidsmate'
  | 'ansatte'
  | 'arbeidstid'
  | 'regler'
  | 'vaktplan'
  | 'tilgang'
  | 'registrert'
  | 'godkjent'
  | 'lonnssystem'
  | 'rapport';
export interface Step {
  key: StepKey;
  phase: Phase;
  done: boolean;
  /** how many are left, where the step is about people */
  count?: number;
  /** whether the caller may do this step (#297 ONB-25); absent from an older server: treated as theirs */
  mine?: boolean;
}

/** Why no tip is due now, in the server's words (services/education.js decide). */
export type EducationWait =
  | 'not_opted_in'
  | 'demo'
  | 'not_yours'
  | 'waiting_for_start'
  | 'deferred'
  | 'nothing_open'
  | 'outside_window'
  | 'cap_day'
  | 'cap_week'
  | 'cap_company'
  | 'cap_task'
  | 'task_gap'
  | 'already_sent'
  | 'unknown';
/** The caller's own choice about tips in this company, and what the sender would do now (#297 ONB-25/26). */
export interface Education {
  opt_in: boolean;
  /** step → YYYY-MM-DD: no tip about that step before the date */
  deferred: Partial<Record<StepKey, string>>;
  /** whether this server sends tips at all; off until the owner turns it on */
  sending: boolean;
  due: string | null;
  wait: EducationWait | null;
  last_sent_at: string | null;
}
export interface Onboarding {
  mode: 'rota' | 'time';
  start_date: string | null;
  revision: number;
  facts: {
    people: number;
    missing_hours: number;
    /** everyone active including the caller (the agreed-week line's total) */
    members?: number;
    uninvited: number;
    logged_in: number;
    published: number;
    /** plan drafts nobody sees yet; kept when the company switches to time-only (ONB-10) */
    drafts?: number;
    first_published: string | null;
    worked: number;
    approved: number;
    reports: number;
    /** a payroll system or «Annet, eller vet ikke» is stored; absent from an older server */
    payroll_chosen?: boolean;
  };
  steps: Step[];
  /** #294 ONB-08: the rule profile, the same answer GET /tenant carries */
  rule_profile: RuleProfile;
  paused_at: string | null;
  /** absent from an older server */
  education?: Education;
  /** the setup's import (#154 HUG-07), the administrator's only; null when none was started */
  import?: SetupDraft | null;
}

const KEY = (t?: number) => ['onboarding', t];

export function useOnboarding(enabled = true) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: KEY(tenant?.id),
    queryFn: () => get<Onboarding>('/onboarding'),
    enabled: enabled && !!tenant,
  });
}

export function useGuidePause() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  return useMutation({
    mutationFn: (paused: boolean) => put<{ paused_at: string | null }>('/onboarding/guide', { paused }),
    onSuccess: (r) =>
      qc.setQueryData<Onboarding>(KEY(tenant?.id), (o) => (o ? { ...o, paused_at: r.paused_at } : o)),
  });
}

/** Tips on or off, and «Minn meg senere» on one step (PUT /onboarding/education); the hub re-reads after. */
export function useEducation() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  return useMutation({
    mutationFn: (body: { opt_in?: boolean; defer?: { step: StepKey; until: string | null } }) =>
      put<Pick<Education, 'opt_in' | 'deferred'>>('/onboarding/education', body),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY(tenant?.id) }),
  });
}

/* ── optional setup drafts (#297 ONB-23/24, X11) ─────────────────────────── */

export type SetupKey = 'lonnskobling' | 'import';
export interface DraftFields {
  inn: string[];
  ut: string[];
  ansvarlig: number | null;
}
/** Where the setup takes the people from (#154 HUG-01): a payroll system, Microsoft 365, a file or by hand. */
export type ImportSource = 'poweroffice' | 'tripletex' | 'visma' | 'microsoft' | 'fil' | 'manuell';
/** The setup's import draft: the source, the source departments taken (checked on the server), reviewed or not. */
export interface ImportFields {
  kilde: ImportSource;
  avdelinger: number[] | null;
  gjennomgatt: boolean;
}
export interface SetupDraft {
  setup: SetupKey;
  system: 'tripletex' | 'poweroffice' | ImportSource;
  /** done: the company has the link, whatever the draft says; none: no draft */
  /** off: a link on record while the link is turned off on this server (#288 review fix 5) */
  status: 'none' | 'draft' | 'paused' | 'done' | 'off';
  fields: DraftFields | ImportFields | null;
  version: number;
  started_at: string | null;
  saved_at: string | null;
  /** who saved it last: the draft is the company's, shared by its administrators (owner 2026-11-04) */
  saved_by?: { id: number; name: string | null } | null;
  paused_at: string | null;
  remind_on: string | null;
}

const DRAFTS = (t?: number, u?: number) => ['setup-drafts', t, u];

/** The company's drafts, shared by its administrators; keyed by company and person, so a switch never shows another company's. */
export function useSetupDrafts(enabled = true) {
  const { tenant } = useTenant();
  const userId = useAuth((s) => s.user?.id);
  return useQuery({
    queryKey: DRAFTS(tenant?.id, userId),
    queryFn: async () => (await get<{ drafts: SetupDraft[] }>('/settings/setup-drafts')).drafts,
    enabled: enabled && !!tenant && userId != null,
  });
}

export interface DraftWrite {
  setup: SetupKey;
  version: number;
  fields?: DraftFields | Partial<Omit<ImportFields, 'avdelinger'>>;
  paused?: boolean;
  remind_on?: string | null;
  discard?: true;
}
/** A stale version answers 409 with the draft as it is now; the caller keeps the person's own edits. */
export function staleDraft(e: unknown): SetupDraft | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const cur = (e.body as { current?: SetupDraft } | undefined)?.current;
  return cur ?? null;
}

export function useSetupDraftWrite() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const userId = useAuth((s) => s.user?.id);
  const key = DRAFTS(tenant?.id, userId);
  return useMutation({
    mutationFn: (body: DraftWrite) => put<SetupDraft>('/settings/setup-draft', body),
    onSuccess: (d) => {
      // the import draft is not listed until it has a source: the first save adds it
      qc.setQueryData<SetupDraft[]>(key, (list) =>
        list?.some((x) => x.setup === d.setup)
          ? list.map((x) => (x.setup === d.setup ? d : x))
          : [...(list ?? []), d],
      );
      if (d.setup === 'import') qc.invalidateQueries({ queryKey: KEY(tenant?.id) });
    },
    onError: (e) => {
      const cur = staleDraft(e);
      if (cur)
        qc.setQueryData<SetupDraft[]>(key, (list) => list?.map((x) => (x.setup === cur.setup ? cur : x)));
    },
  });
}

/* ── the setup's source departments (#154 OP-04, HUG-02) ─────────────────── */

/** One source department as the server judged it; id 0 is the people on no department. */
export interface ScopeDepartment {
  id: number;
  code: string;
  name: string;
  people: number;
  state: 'eligible' | 'other_store' | 'outside' | 'empty';
}
export interface ImportScope {
  system: 'poweroffice' | 'tripletex' | null;
  state: 'ready' | 'unsupported' | 'not_connected' | 'department_needed';
  departments: ScopeDepartment[];
  /** what is saved; null before the first save */
  selected: number[] | null;
  /** the store's own department, else every eligible one; never by a name */
  suggested: number[];
  version: number;
}
const SCOPE = (t?: number) => ['import-scope', t];

export function useImportScope(enabled = true) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: SCOPE(tenant?.id),
    queryFn: () => get<ImportScope>('/onboarding/import-scope'),
    enabled: enabled && !!tenant,
    staleTime: 0,
  });
}

/** Saves the departments with the version read; a refusal keeps the person's ticks (the caller's state). */
export function useImportScopeWrite() {
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const userId = useAuth((s) => s.user?.id);
  return useMutation({
    mutationFn: (body: { departments: number[]; version: number }) =>
      put<SetupDraft>('/onboarding/import-scope', body),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: SCOPE(tenant?.id) });
      qc.setQueryData<SetupDraft[]>(DRAFTS(tenant?.id, userId), (list) =>
        list?.some((x) => x.setup === d.setup)
          ? list.map((x) => (x.setup === d.setup ? d : x))
          : [...(list ?? []), d],
      );
    },
  });
}
